import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const EventLanding = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [isRegistered, setIsRegistered] = useState(false);
  const [email, setEmail] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Set your event date here
  const eventDate = new Date('2025-08-15T18:00:00').getTime();

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = eventDate - now;

      if (distance > 0) {
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [eventDate]);

  const handleRegistration = (e) => {
    e.preventDefault();
    if (email) {
      // Send email with ticket link
      const ticketLink = 'https://www.eventbrite.com/e/soulful-teej-yatra-usa-tour-2025-tickets';
      const subject = 'Soulful Teej Yatra USA Tour 2025 - Ticket Information';
      const body = `Dear Cultural Enthusiast,

Thank you for your interest in the Soulful Teej Yatra USA Tour 2025!

🎭 Event Details:
- Featuring: Asmita Adhikari & Mahesh Kafle
- Tour Dates: August - October 2025
- Multiple cities across America

🎟️ Get Your Tickets:
${ticketLink}

📞 For questions or group bookings:
- Call: (571) 355-5165
- Call: (703) 509-1127

We can't wait to celebrate with you!

Best regards,
3E Entertainment Team`;

      const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailtoLink;
      
      setIsRegistered(true);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 60, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };

  const floatingVariants = {
    animate: {
      y: [0, -20, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    }
  };

  return (
    <div className="event-landing">
      
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-overlay" />
          <div className="floating-elements">
            <motion.div 
              className="floating-element element-1"
              variants={floatingVariants}
              animate="animate"
            />
            <motion.div 
              className="floating-element element-2"
              variants={floatingVariants}
              animate="animate"
              style={{ animationDelay: '1s' }}
            />
            <motion.div 
              className="floating-element element-3"
              variants={floatingVariants}
              animate="animate"
              style={{ animationDelay: '2s' }}
            />
          </div>
        </div>

        <div className="hero-content">
          <motion.div
            className="hero-text"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            
            <motion.h1 variants={itemVariants} className="hero-title">
              SOULFUL TEEJ YATRA
              <span className="highlight">USA TOUR 2025</span>
            </motion.h1>
            
            <motion.p variants={itemVariants} className="hero-description">
              Experience the vibrant celebration of Nepali culture with powerhouse performances by Asmita Adhikari and Mahesh Kafle. Join us for an unforgettable musical journey across America.
            </motion.p>

            <motion.div variants={itemVariants} className="hero-details">
              <div className="detail-item">
                <span className="detail-icon">📅</span>
                <span>August - October 2025</span>
              </div>
              <div className="detail-item">
                <span className="detail-icon">🎭</span>
                <span>Multiple Cities Across USA</span>
              </div>
              <div className="detail-item">
                <span className="detail-icon">🎵</span>
                <span>Live Performances & Cultural Dance</span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Hero CTA Buttons */}
        <motion.div 
          className="hero-cta-section"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <div className="hero-buttons">
            <motion.button
              className="hero-btn primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get Tickets Now
            </motion.button>
            <motion.button
              className="hero-btn secondary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Learn More
            </motion.button>
          </div>
        </motion.div>
      </section>

      {/* Countdown Section */}
      <section className="countdown-section">
        <div className="container">
          <motion.div
            className="countdown-wrapper"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="countdown-content">
              <h2 className="countdown-main-title">The Journey Begins Soon</h2>
              <p className="countdown-subtitle">Join us for an unforgettable celebration of Nepali culture across America</p>
              
              <div className="countdown-display">
                <div className="time-block">
                  <motion.div 
                    className="time-number"
                    key={timeLeft.days}
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {timeLeft.days}
                  </motion.div>
                  <div className="time-label">Days</div>
                </div>
                <div className="time-separator">:</div>
                <div className="time-block">
                  <motion.div 
                    className="time-number"
                    key={timeLeft.hours}
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {timeLeft.hours}
                  </motion.div>
                  <div className="time-label">Hours</div>
                </div>
                <div className="time-separator">:</div>
                <div className="time-block">
                  <motion.div 
                    className="time-number"
                    key={timeLeft.minutes}
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {timeLeft.minutes}
                  </motion.div>
                  <div className="time-label">Minutes</div>
                </div>
                <div className="time-separator">:</div>
                <div className="time-block">
                  <motion.div 
                    className="time-number"
                    key={timeLeft.seconds}
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {timeLeft.seconds}
                  </motion.div>
                  <div className="time-label">Seconds</div>
                </div>
              </div>

              <div className="event-highlights">
                <div className="highlight-item">
                  <div className="highlight-icon">🎭</div>
                  <div className="highlight-text">
                    <strong>Cultural Extravaganza</strong>
                    <span>Traditional dances, folk music, and authentic performances</span>
                  </div>
                </div>
                <div className="highlight-item">
                  <div className="highlight-icon">🎵</div>
                  <div className="highlight-text">
                    <strong>Star Performers</strong>
                    <span>Asmita Adhikari & Mahesh Kafle live on stage</span>
                  </div>
                </div>
                <div className="highlight-item">
                  <div className="highlight-icon">🌍</div>
                  <div className="highlight-text">
                    <strong>National Tour</strong>
                    <span>6 cities across America, August - October 2025</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Event Details Section */}
      <section className="event-details-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Experience the Magic</h2>
            <p className="section-subtitle">
              Immerse yourself in the rich traditions and vibrant culture of Nepal through an extraordinary musical journey
            </p>
          </motion.div>

          <motion.div
            className="details-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div variants={itemVariants} className="detail-card featured">
              <div className="card-overlay"></div>
              <div className="card-image">
                <img src="/images/artist.webp" alt="Artists" loading="lazy" decoding="async" />
              </div>
              <div className="card-content">
                <div className="card-badge">Featured</div>
                <h3>World-Class Artists</h3>
                <p>Asmita Adhikari and Mahesh Kafle, two of Nepal's most celebrated voices, will deliver soul-stirring performances that capture the essence of our cultural heritage.</p>
                <ul className="feature-list">
                  <li>Original compositions & traditional favorites</li>
                  <li>Interactive cultural storytelling</li>
                  <li>Authentic Nepali musical instruments</li>
                </ul>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="detail-card">
              <div className="card-overlay"></div>
              <div className="card-image">
                <img src="/images/R5BI6467.webp" alt="Cultural Dance" loading="lazy" decoding="async" />
              </div>
              <div className="card-content">
                <div className="card-badge">Cultural</div>
                <h3>Traditional Performances</h3>
                <p>Experience the vibrant colors and rhythmic movements of authentic Nepali folk dances, each telling a story of our rich cultural tapestry.</p>
                <ul className="feature-list">
                  <li>Regional folk dances from across Nepal</li>
                  <li>Traditional costume presentations</li>
                  <li>Audience participation segments</li>
                </ul>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="detail-card">
              <div className="card-overlay"></div>
              <div className="card-image">
                <img src="/images/event-pic.png" alt="Community" loading="lazy" decoding="async" />
              </div>
              <div className="card-content">
                <div className="card-badge">Community</div>
                <h3>Cultural Connection</h3>
                <p>Join thousands of community members in celebrating our shared heritage, creating lasting bonds and preserving our traditions for future generations.</p>
                <ul className="feature-list">
                  <li>Networking opportunities</li>
                  <li>Cultural food experiences</li>
                  <li>Photo opportunities & memories</li>
                </ul>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Registration Section */}
      <section className="registration-section">
        <div className="registration-background">
          <div className="registration-overlay" />
        </div>
        <div className="container">
          <motion.div
            className="registration-content"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="registration-title">
              Reserve Your Spot Today
            </h2>
            <p className="registration-description">
              Don't miss this spectacular celebration of Nepali culture. Register now to secure your place at this exclusive event.
            </p>

            <AnimatePresence>
              {showSuccess && (
                <motion.div
                  className="success-message"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  🎉 Thank you for registering! We'll be in touch soon.
                </motion.div>
              )}
            </AnimatePresence>

            <form onSubmit={handleRegistration} className="registration-form">
              <div className="form-group">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="email-input"
                  required
                />
                <motion.button
                  type="submit"
                  className="register-button"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  disabled={isRegistered}
                >
                  {isRegistered ? 'Registered ✓' : 'Register Now'}
                </motion.button>
              </div>
            </form>

            <div className="contact-info">
              <p>For tickets and sponsorship inquiries:</p>
              <div className="contact-details">
                <a href="tel:+15713555165" className="contact-link">
                  📞 (571) 355-5165
                </a>
                <a href="tel:+17035091127" className="contact-link">
                  📞 (703) 509-1127
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tour Schedule Section */}
      <section className="schedule-section">
        <div className="container">
          <motion.div
            className="schedule-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="schedule-title">Tour Schedule</h2>
            <p className="schedule-description">
              We're bringing the celebration to cities across America
            </p>
          </motion.div>

          <motion.div
            className="schedule-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {[
              { city: 'New York', date: 'August 15, 2025', venue: 'Lincoln Center' },
              { city: 'Washington DC', date: 'August 22, 2025', venue: 'Kennedy Center' },
              { city: 'Chicago', date: 'September 5, 2025', venue: 'Symphony Center' },
              { city: 'Los Angeles', date: 'September 12, 2025', venue: 'Hollywood Bowl' },
              { city: 'San Francisco', date: 'September 19, 2025', venue: 'Davies Hall' },
              { city: 'Dallas', date: 'October 3, 2025', venue: 'Meyerson Center' }
            ].map((event, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="schedule-card"
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <div className="schedule-date">{event.date}</div>
                <div className="schedule-city">{event.city}</div>
                <div className="schedule-venue">{event.venue}</div>
                <button className="schedule-button">Get Tickets</button>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <style>{`
        .event-landing {
          min-height: 100vh;
          background: #000;
          color: white;
          overflow-x: hidden;
        }

        .hero-section {
          position: relative;
          height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          overflow: hidden;
        }

        .hero-background {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('/images/ad.jpg');
          background-size: cover;
          background-position: center;
        }

        .hero-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.4);
        }

        .floating-elements {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
        }

        .floating-element {
          position: absolute;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
        }

        .element-1 {
          width: 100px;
          height: 100px;
          top: 20%;
          left: 10%;
          animation-delay: 0s;
        }

        .element-2 {
          width: 60px;
          height: 60px;
          top: 60%;
          right: 15%;
          animation-delay: 1s;
        }

        .element-3 {
          width: 80px;
          height: 80px;
          bottom: 20%;
          left: 20%;
          animation-delay: 2s;
        }

        .hero-content {
          position: relative;
          z-index: 2;
          max-width: 1200px;
          padding: 0 20px;
          margin: 0 auto;
        }


        .hero-title {
          font-size: clamp(2.5rem, 6vw, 5rem);
          font-weight: 900;
          line-height: 1.1;
          margin-bottom: 20px;
          color: white;
        }

        .highlight {
          display: block;
          color: #C72631;
          margin-top: 10px;
        }

        .hero-description {
          font-size: clamp(1.1rem, 2vw, 1.4rem);
          line-height: 1.6;
          margin-bottom: 40px;
          color: rgba(255, 255, 255, 0.9);
          max-width: 800px;
          margin-left: auto;
          margin-right: auto;
        }

        .hero-details {
          display: flex;
          justify-content: center;
          gap: 40px;
          margin-bottom: 60px;
          flex-wrap: wrap;
        }

        .detail-item {
          display: flex;
          align-items: center;
          gap: 10px;
          color: rgba(255, 255, 255, 0.8);
          font-weight: 500;
        }

        .detail-icon {
          font-size: 1.2rem;
        }

        .hero-cta-section {
          position: absolute;
          bottom: 80px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 3;
        }

        .hero-buttons {
          display: flex;
          gap: 20px;
          justify-content: center;
          align-items: center;
        }

        .hero-btn {
          padding: 16px 32px;
          border: none;
          border-radius: 8px;
          font-size: 1.1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          letter-spacing: 0.5px;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
        }

        .hero-btn.primary {
          background: #C72631;
          color: white;
        }

        .hero-btn.secondary {
          background: rgba(255, 255, 255, 0.15);
          color: white;
          border: 2px solid rgba(255, 255, 255, 0.3);
        }

        .hero-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 35px rgba(0, 0, 0, 0.4);
        }

        .countdown-section {
          background: #0a0a0a;
          padding: 120px 0;
          position: relative;
          overflow: hidden;
        }

        .countdown-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('/images/R5BI6534.webp');
          background-size: cover;
          background-position: center;
          opacity: 0.1;
        }

        .countdown-wrapper {
          position: relative;
          z-index: 2;
        }

        .countdown-content {
          text-align: center;
          max-width: 1000px;
          margin: 0 auto;
          color: white;
        }

        .countdown-main-title {
          font-size: clamp(2.5rem, 5vw, 4rem);
          font-weight: 900;
          margin-bottom: 20px;
          color: white;
        }

        .countdown-subtitle {
          font-size: 1.3rem;
          margin-bottom: 50px;
          color: rgba(255, 255, 255, 0.9);
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
          line-height: 1.6;
        }

        .countdown-display {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 20px;
          margin-bottom: 60px;
          flex-wrap: wrap;
        }

        .time-block {
          background: rgba(255, 255, 255, 0.06);
          border-radius: 12px;
          padding: 25px 20px;
          text-align: center;
          min-width: 100px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        }

        .time-number {
          font-size: 3rem;
          font-weight: 900;
          color: #C72631;
          line-height: 1;
          margin-bottom: 8px;
        }

        .time-label {
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.8);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .time-separator {
          font-size: 2rem;
          font-weight: 700;
          color: white;
        }

        .event-highlights {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 30px;
          margin-top: 40px;
        }

        .highlight-item {
          background: rgba(255, 255, 255, 0.06);
          border-radius: 12px;
          padding: 25px;
          border: 1px solid rgba(255, 255, 255, 0.15);
          display: flex;
          align-items: center;
          gap: 20px;
          transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .highlight-item:hover {
          transform: translateY(-5px);
        }

        .highlight-icon {
          font-size: 2.5rem;
          min-width: 60px;
          text-align: center;
        }

        .highlight-text strong {
          display: block;
          font-size: 1.2rem;
          font-weight: 700;
          margin-bottom: 5px;
          color: white;
        }

        .highlight-text span {
          color: rgba(255, 255, 255, 0.8);
          font-size: 0.95rem;
        }

        .event-details-section {
          background: #f7f7f7;
          padding: 120px 0;
          color: #0a0a0a;
          position: relative;
          overflow: hidden;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
          position: relative;
          z-index: 1;
        }

        .section-header {
          text-align: center;
          margin-bottom: 80px;
        }

        .section-title {
          font-size: clamp(2.5rem, 5vw, 4rem);
          font-weight: 900;
          margin-bottom: 20px;
          color: #0a0a0a;
          position: relative;
        }

        .section-title::after {
          content: '';
          position: absolute;
          bottom: -10px;
          left: 50%;
          transform: translateX(-50%);
          width: 80px;
          height: 4px;
          background: #C72631;
          border-radius: 2px;
        }

        .section-subtitle {
          font-size: 1.2rem;
          color: #6c757d;
          max-width: 700px;
          margin: 0 auto;
          line-height: 1.6;
        }

        .details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
          gap: 40px;
        }

        .detail-card {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
          transition: all 0.4s cubic-bezier(0.22, 1, 0.36, 1);
          position: relative;
          border: 2px solid transparent;
        }

        .detail-card.featured {
          border-color: #C72631;
          transform: scale(1.02);
        }

        .detail-card:hover {
          transform: translateY(-15px) scale(1.02);
          box-shadow: 0 30px 60px rgba(0, 0, 0, 0.15);
        }

        .card-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.03);
          opacity: 0;
          transition: opacity 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          z-index: 1;
        }

        .detail-card:hover .card-overlay {
          opacity: 1;
        }

        .card-image {
          height: 280px;
          overflow: hidden;
          position: relative;
        }

        .card-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .detail-card:hover .card-image img {
          transform: scale(1.08);
        }

        .card-content {
          padding: 35px;
          position: relative;
          z-index: 2;
        }

        .card-badge {
          background: #C72631;
          color: white;
          padding: 6px 16px;
          border-radius: 8px;
          font-size: 0.8rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          display: inline-block;
          margin-bottom: 15px;
        }

        .card-content h3 {
          font-size: 1.6rem;
          font-weight: 800;
          margin-bottom: 15px;
          color: #0a0a0a;
          line-height: 1.3;
        }

        .card-content p {
          color: #6c757d;
          line-height: 1.7;
          font-size: 1rem;
          margin-bottom: 20px;
        }

        .feature-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .feature-list li {
          color: #495057;
          padding: 8px 0;
          position: relative;
          padding-left: 25px;
          font-size: 0.95rem;
          line-height: 1.5;
        }

        .feature-list li::before {
          content: '✓';
          position: absolute;
          left: 0;
          color: #C72631;
          font-weight: 700;
          font-size: 1.1rem;
        }

        .registration-section {
          position: relative;
          padding: 120px 0;
          color: white;
          overflow: hidden;
        }

        .registration-background {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(10, 10, 10, 0.85) url('/images/R5BI6877.webp');
          background-size: cover;
          background-position: center;
          background-blend-mode: darken;
        }

        .registration-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
        }

        .registration-content {
          position: relative;
          z-index: 2;
          text-align: center;
          max-width: 900px;
          margin: 0 auto;
        }

        .registration-title {
          font-size: clamp(2.5rem, 5vw, 3.5rem);
          font-weight: 900;
          margin-bottom: 25px;
          color: white;
          position: relative;
        }

        .registration-title::after {
          content: '';
          position: absolute;
          bottom: -15px;
          left: 50%;
          transform: translateX(-50%);
          width: 100px;
          height: 4px;
          background: #C72631;
          border-radius: 2px;
        }

        .registration-description {
          font-size: 1.3rem;
          line-height: 1.6;
          margin-bottom: 50px;
          color: rgba(255, 255, 255, 0.9);
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
        }

        .success-message {
          background: #28a745;
          color: white;
          padding: 20px 40px;
          border-radius: 15px;
          margin-bottom: 40px;
          font-weight: 600;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .registration-form {
          margin-bottom: 60px;
        }

        .form-group {
          display: flex;
          max-width: 600px;
          margin: 0 auto;
          gap: 20px;
          background: rgba(255, 255, 255, 0.1);
          padding: 10px;
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .email-input {
          flex: 1;
          padding: 20px 30px;
          border: none;
          border-radius: 8px;
          font-size: 1.1rem;
          outline: none;
          background: rgba(255, 255, 255, 0.95);
          color: #0a0a0a;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
        }

        .email-input::placeholder {
          color: #6c757d;
        }

        .register-button {
          background: #C72631;
          color: white;
          border: none;
          padding: 20px 40px;
          border-radius: 8px;
          font-weight: 700;
          font-size: 1.1rem;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
          white-space: nowrap;
          min-width: 180px;
        }

        .register-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 35px rgba(0, 0, 0, 0.4);
        }

        .register-button:disabled {
          background: #28a745;
          cursor: not-allowed;
          transform: none;
        }

        .contact-info {
          text-align: center;
          background: rgba(255, 255, 255, 0.1);
          padding: 40px;
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .contact-info p {
          margin-bottom: 20px;
          color: rgba(255, 255, 255, 0.9);
          font-size: 1.1rem;
          font-weight: 500;
        }

        .contact-details {
          display: flex;
          justify-content: center;
          gap: 20px;
          flex-wrap: wrap;
        }

        .contact-link {
          color: white;
          text-decoration: none;
          font-weight: 600;
          background: #C72631;
          padding: 15px 25px;
          border-radius: 8px;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .contact-link:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4);
        }

        .schedule-section {
          background: #f7f7f7;
          padding: 100px 0;
          color: #0a0a0a;
        }

        .schedule-header {
          text-align: center;
          margin-bottom: 60px;
        }

        .schedule-title {
          font-size: clamp(2.5rem, 5vw, 3.5rem);
          font-weight: 900;
          margin-bottom: 20px;
          color: #0a0a0a;
        }

        .schedule-description {
          font-size: 1.2rem;
          color: #6c757d;
          max-width: 600px;
          margin: 0 auto;
        }

        .schedule-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 30px;
        }

        .schedule-card {
          background: white;
          border-radius: 12px;
          padding: 30px;
          text-align: center;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          cursor: pointer;
        }

        .schedule-card:hover {
          box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
        }

        .schedule-date {
          color: #C72631;
          font-weight: 700;
          font-size: 1rem;
          margin-bottom: 10px;
        }

        .schedule-city {
          font-size: 1.5rem;
          font-weight: 800;
          color: #0a0a0a;
          margin-bottom: 10px;
        }

        .schedule-venue {
          color: #6c757d;
          margin-bottom: 20px;
        }

        .schedule-button {
          background: #C72631;
          color: white;
          border: none;
          padding: 12px 25px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
        }

        .schedule-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .hero-section {
            height: 90vh;
          }


          .hero-details {
            flex-direction: column;
            gap: 20px;
          }

          .hero-cta-section {
            bottom: 40px;
            left: 20px;
            right: 20px;
            transform: none;
          }

          .hero-buttons {
            flex-direction: column;
            gap: 15px;
          }

          .hero-btn {
            width: 100%;
            padding: 14px 24px;
            font-size: 1rem;
          }

          .countdown-section {
            padding: 80px 0;
          }

          .countdown-main-title {
            font-size: 2.5rem;
          }

          .countdown-subtitle {
            font-size: 1.1rem;
            margin-bottom: 40px;
          }

          .countdown-display {
            gap: 10px;
            margin-bottom: 40px;
          }

          .time-block {
            min-width: 70px;
            padding: 15px 10px;
          }

          .time-number {
            font-size: 2rem;
          }

          .time-separator {
            display: none;
          }

          .event-highlights {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .highlight-item {
            flex-direction: column;
            text-align: center;
            padding: 20px;
          }

          .highlight-icon {
            margin-bottom: 10px;
          }

          .event-details-section {
            padding: 80px 0;
          }

          .section-header {
            margin-bottom: 60px;
          }

          .section-title {
            font-size: 2.5rem;
          }

          .section-subtitle {
            font-size: 1rem;
          }

          .details-grid {
            grid-template-columns: 1fr;
            gap: 30px;
          }

          .detail-card.featured {
            transform: none;
          }

          .card-content {
            padding: 25px;
          }

          .registration-section {
            padding: 80px 0;
          }

          .registration-title {
            font-size: 2.5rem;
          }

          .registration-description {
            font-size: 1.1rem;
            margin-bottom: 40px;
          }

          .form-group {
            flex-direction: column;
            gap: 15px;
            padding: 8px;
          }

          .email-input {
            padding: 16px 20px;
            font-size: 1rem;
          }

          .register-button {
            padding: 16px 30px;
            font-size: 1rem;
            min-width: auto;
          }

          .contact-info {
            padding: 30px 20px;
          }

          .contact-details {
            flex-direction: column;
            gap: 15px;
          }

          .contact-link {
            padding: 12px 20px;
            justify-content: center;
          }

          .schedule-grid {
            grid-template-columns: 1fr;
          }

          .floating-element {
            display: none;
          }
        }

        @media (max-width: 480px) {
          .hero-content {
            padding: 0 15px;
          }

          .countdown-container {
            left: 15px;
            right: 15px;
            padding: 15px;
          }

          .event-details-section,
          .registration-section,
          .schedule-section {
            padding: 60px 0;
          }

          .container {
            padding: 0 15px;
          }
        }

        /* Animations */
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        .register-button:not(:disabled):hover {
          animation: pulse 0.6s ease-in-out;
        }

        /* High contrast mode */
        @media (prefers-contrast: high) {
          .hero-title,
          .registration-title {
            color: white;
          }
        }

        /* Reduced motion */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default EventLanding; 